'use client';

import { useTranslation } from 'react-i18next';

import {
  bucketByCategory,
  PHOTO_CATEGORIES,
  type PhotoCategory,
} from '@kit/artists';
import {
  cdnImagePlaceholder,
  dancerImageSrc,
  dancerImageSrcSet,
} from '@kit/shared/storage';
import {
  ImageLightbox,
  useImageViewer,
  type ViewerImage,
} from '@kit/ui/image-viewer';

import { BlurUpImage } from './blur-up-image';
import { DancerSection } from './dancer-section';
import { isRenderableMediaUrl } from './media-url';
import type { DancerMediaItem } from './types';

interface Props {
  photos: DancerMediaItem[];
  /** Descriptive, SEO-friendly alt text for every gallery tile (BAL-623).
   * Composed once from the dancer's profile by `buildDancerImageAlt` and
   * passed down so the same string feeds the `<img alt>`, the hero, and the
   * image sitemap (Critical Rule #8 — one edit surface). */
  imageAlt: string;
  /** When true, render only the bucketed photo grids without the outer
   * `DancerSection` chrome. Used by `DancerAdditionalMedia` (§13) so the
   * overflow photos sit under a shared "Additional Media" heading. */
  bare?: boolean;
}

/**
 * Editorial gallery — photos bucketed by their editorial category.
 *
 * Section order (spec, 2026-05-16):
 *   1. Audition / studio
 *   2. Stage
 *   3. Brand / editorial
 *   4. Other (legacy Meteor values + uncategorised)
 *
 * Empty sections silently omit. The hero-4:3 + portrait-4:5 layout
 * (preserved from the pre-bucketing render) applies to the FIRST
 * non-empty section's first photo. Subsequent sections are uniform
 * 4:5 portraits.
 *
 * BAL-370: clicking any tile opens the photo in the canonical
 * `@kit/ui/image-viewer` lightbox. The viewer indexes over the photos in
 * RENDER order (the same flattened, bucket-ordered sequence the grid
 * paints), so the lightbox's prev/next walks the gallery left-to-right,
 * top-to-bottom.
 */
export function DancerGallery({ photos, imageAlt, bare = false }: Props) {
  const { t } = useTranslation();
  const buckets = bucketByCategory(
    photos,
    PHOTO_CATEGORIES.map((c) => c.value),
  );

  // Render order: spec categories first, then Other.
  const orderedSections: Array<{
    key: PhotoCategory | 'other';
    label: string;
    items: DancerMediaItem[];
  }> = [
    ...PHOTO_CATEGORIES.map((c) => ({
      key: c.value as PhotoCategory,
      label: c.label,
      items: buckets.get(c.value) ?? [],
    })),
    {
      key: 'other' as const,
      label: t('dancer:publicView.media.other'),
      items: buckets.get(null) ?? [],
    },
  ];

  const nonEmpty = orderedSections.filter((s) => s.items.length > 0);

  // Photos in the exact order they're painted, so the tile's render index
  // maps 1:1 onto the lightbox index. Only fetchable (http(s)) sources are
  // rendered, so the viewer images must be filtered identically.
  const orderedPhotos = nonEmpty.flatMap((s) => s.items);
  const viewerImages: ViewerImage[] = orderedPhotos.flatMap((p) => {
    const src = p.signed_url ?? '';
    if (!isRenderableMediaUrl(src)) return [];
    return [
      {
        id: p.id,
        src,
        thumbnailSrc: p.thumbnail_url ?? undefined,
        alt: p.file_name ?? undefined,
        fileName: p.file_name ?? undefined,
      },
    ];
  });

  const viewer = useImageViewer(viewerImages);

  // Map a media-item id to its index in the (filtered) viewer image list so a
  // tile click opens the lightbox at the right photo even when some unfetchable
  // rows were dropped from `viewerImages`.
  const viewerIndexById = new Map<string, number>(
    viewerImages.map((img, i) => [img.id as string, i]),
  );

  // Hooks must run unconditionally, so the early-return guards come AFTER them.
  if (photos.length === 0) return null;
  if (nonEmpty.length === 0) return null;

  // Identify the first non-empty section so its first photo gets the
  // hero-4:3 layout. After that, every photo is a uniform 4:5 portrait.
  const firstSectionKey = nonEmpty[0]!.key;

  const grids = (
    <div className="space-y-12">
      {nonEmpty.map((section) => (
        <GallerySection
          key={section.key}
          label={section.label}
          items={section.items}
          imageAlt={imageAlt}
          showHeroFirst={section.key === firstSectionKey}
          showLabel={nonEmpty.length > 1}
          viewerIndexById={viewerIndexById}
          onOpen={viewer.openAt}
        />
      ))}
    </div>
  );

  const lightbox =
    viewerImages.length > 0 ? (
      <ImageLightbox
        images={viewerImages}
        index={viewer.index}
        onIndexChange={viewer.goTo}
        open={viewer.isOpen}
        onOpenChange={viewer.setIsOpen}
        showDownload={false}
      />
    ) : null;

  // §13: when embedded inside the shared "Additional Media" section, skip
  // this component's own section chrome.
  if (bare) {
    return (
      <>
        {grids}
        {lightbox}
      </>
    );
  }

  return (
    <DancerSection
      eyebrow={t('dancer:publicView.gallery.eyebrow')}
      title={t('dancer:publicView.gallery.title')}
      count={photos.length}
    >
      {grids}
      {lightbox}
    </DancerSection>
  );
}

function GallerySection({
  label,
  items,
  imageAlt,
  showHeroFirst,
  showLabel,
  viewerIndexById,
  onOpen,
}: {
  label: string;
  items: DancerMediaItem[];
  imageAlt: string;
  showHeroFirst: boolean;
  showLabel: boolean;
  viewerIndexById: Map<string, number>;
  onOpen: (index: number) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {showLabel && (
        <h3 className="text-foreground/60 font-mono text-[10px] tracking-[0.22em] uppercase">
          {label}
        </h3>
      )}
      <ul className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-6">
        {items.map((p, i) => {
          // signed_url is populated by the loader from external_url
          // (Meteor S3) or a freshly-minted Supabase signed URL.
          // Raw storage_path is not browser-fetchable.
          const rawUrl = p.signed_url ?? '';
          const isFetchable = isRenderableMediaUrl(rawUrl);
          // First photo of the FIRST non-empty section is the hero
          // (lg: 4 cols, 4:3); the rest are 2-col 4:5.
          const isHero = showHeroFirst && i === 0;
          // R2 → Cloudflare-resized; Supabase-signed → untouched. Widths,
          // fit and quality all come from the shared `DancerImageSlots`
          // registry (`@kit/shared/storage`), which the image sitemap reads
          // too — one composer, so a sitemap image loc is byte-identical to
          // the `src` rendered here (Critical Rule #8) and no extra billable
          // Cloudflare transform is minted. Clean 2× width steps; `src`
          // reuses one of them because crawlers ignore `srcset` and fetch
          // `src`. `scale-down` (not `cover`) because these are width-only
          // transforms and Cloudflare's `cover` needs BOTH dimensions — the
          // `<img>` already has CSS `object-cover` against the tile's fixed
          // aspect-ratio, so the crop happens in the browser.
          const slot = isHero ? 'GALLERY_LEAD' : 'GALLERY_TILE';
          const srcSet = dancerImageSrcSet(rawUrl, slot);
          const url = dancerImageSrc(rawUrl, slot) ?? rawUrl;
          // LQIP blur-up: derive the placeholder from the RAW (pre-transform)
          // URL so R2 detection runs on the stable cdn.ballee.co source, not
          // the already-transformed `url`. Null for Supabase-signed sources.
          const placeholder = cdnImagePlaceholder(rawUrl);
          const sizes = isHero
            ? '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 67vw'
            : '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 16vw';
          const viewerIndex = viewerIndexById.get(p.id);
          return (
            <li
              key={p.id}
              data-media-id={p.id}
              className={
                isHero
                  ? 'bg-foreground/5 col-span-2 aspect-[4/3] overflow-hidden lg:col-span-4'
                  : 'bg-foreground/5 aspect-[4/5] overflow-hidden lg:col-span-2'
              }
            >
              {isFetchable &&
                (viewerIndex !== undefined ? (
                  // BAL-370: open the canonical lightbox at this photo.
                  // `aria-hidden` on the inner image keeps the gallery
                  // decorative for screen readers (raw filenames are noise);
                  // the button itself carries the accessible label.
                  // eslint-disable-next-line kit-components/use-kit-primitives -- full-bleed zoom-in image tile: the BlurUpImage fills h-full w-full and kit Button's base px-4 py-2 padding would inset it, with no size variant that removes the base padding
                  <button
                    type="button"
                    onClick={() => onOpen(viewerIndex)}
                    aria-label={t('dancer:publicView.gallery.viewPhoto')}
                    className="focus-visible:ring-ring block h-full w-full cursor-zoom-in focus:outline-none focus-visible:ring-2"
                  >
                    {/* Descriptive alt from the dancer's profile (BAL-623) —
                        the crawlable Google Images signal. Not aria-hidden:
                        the button's aria-label supplies the accessible NAME
                        (announced on focus), while the alt describes the
                        image content for image search + AT deep-reads. */}
                    <BlurUpImage
                      src={url}
                      srcSet={srcSet ?? undefined}
                      sizes={sizes}
                      alt={imageAlt}
                      placeholder={placeholder}
                      className="block h-full w-full transition duration-300 hover:scale-[1.03]"
                      loading={i < 3 ? 'eager' : 'lazy'}
                      fetchPriority={isHero ? 'high' : undefined}
                    />
                  </button>
                ) : (
                  <BlurUpImage
                    src={url}
                    srcSet={srcSet ?? undefined}
                    sizes={sizes}
                    // Descriptive alt composed from the dancer's profile
                    // (BAL-623): the previous empty alt (from noisy raw bucket
                    // filenames) gave Google Images no signal. `buildDancerImageAlt`
                    // yields e.g. "Naomi — Ballet dancer, Paris, France".
                    alt={imageAlt}
                    placeholder={placeholder}
                    className="block h-full w-full"
                    loading={i < 3 ? 'eager' : 'lazy'}
                    fetchPriority={isHero ? 'high' : undefined}
                  />
                ))}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
